import { isGatewayStatus } from "../../helpers/errors.helpers";
import { apiBaseUrl } from "../../helpers/request.helpers";

// Sends no token and reads no storage: any HTTP answer other than 502/503/504
// proves the backend is up, and a 401 here never reaches QueryCache.onError,
// so the poll can never trigger a logout.
export async function pingBackend(
  fetchImpl: typeof fetch = fetch,
  baseUrl: string = apiBaseUrl(),
): Promise<boolean> {
  try {
    const response = await fetchImpl(`${baseUrl}/frontend/get-session-user`, {
      method: "GET",
      cache: "no-store",
      signal: AbortSignal.timeout(5000),
    });
    return !isGatewayStatus(response.status);
  } catch {
    return false;
  }
}

export type RecoveryPollOptions = {
  ping: () => Promise<boolean>;
  onAlive: () => void;
  initialDelay?: number;
  factor?: number;
  maxDelay?: number;
};

export function startRecoveryPoll({
  ping,
  onAlive,
  initialDelay = 3000,
  factor = 1.5,
  maxDelay = 15000,
}: RecoveryPollOptions): () => void {
  let stopped = false;
  let delay = initialDelay;
  let timer: ReturnType<typeof setTimeout> | undefined;

  async function tick() {
    const alive = await ping();
    if (stopped) return;
    if (alive) {
      stopped = true;
      onAlive();
      return;
    }
    delay = Math.min(delay * factor, maxDelay);
    timer = setTimeout(tick, delay);
  }

  timer = setTimeout(tick, delay);

  return () => {
    stopped = true;
    clearTimeout(timer);
  };
}
